import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
/**
 * Master Data → Stores → Store Requests.
 *
 * Stores that field employees and managers submitted from the mobile app ("Add a
 * store"). Reviewing one opens the store exactly as submitted, editable. Approving
 * creates it — it then appears on the Stores tab — and maps the requester to it through
 * the same deploy rule every other path uses: added alongside their other stores for a
 * multi-store role, moved otherwise.
 *
 * Rendered as a tab of StoreMasterPage, which only shows the tab once the server has
 * confirmed the `web.store_requests` page right (its page’s tick).
 *
 * After an approval, the green "Store created…" notice offers "Raise hiring for it" (or
 * "Ask for hiring for it") when hiring may be raised or asked for in this project and the
 * new store is Active with no staffing hold. It opens the Raise hiring window for that
 * store with the reason New store, as the Stores tab's "Store added" step does.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ExternalLink, MapPin, RefreshCw } from 'lucide-react';
import Modal from '../components/Modal';
import { InlineLoader } from '../components/loading';
import StoreCustomFieldInputs from '../components/StoreCustomFieldInputs';
import {
  categoriesAPI,
  locationsAPI,
  storeCustomFieldsAPI,
  storeRequestsAPI,
  storeTradesAPI,
  type StoreCustomField,
  type StoreRequest,
  type StoreRequestApproval,
  type StoreRequestApproved,
  type StoreRequestStatus,
} from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { normalizeLocationKey } from '../utils/locationKey';
import { RaiseHiringModal, type RaiseHiringSubject } from '../components/hiring/RaiseHiringModal';
import { STORE_ADDED_WORDS } from '../components/hiring/hiringText';
import { newStoreRaiseMode, type NewStoreRaiseMode } from '../components/hiring/newStoreRaise';
import { forgetHiringAccess, hiringUserKeyOf, useHiringAccess } from '../components/hiring/useHiringAccess';
import { forgetHiringRequests, useHiringRequests } from '../components/hiring/useHiringRequests';

/** The store an approval created, as the Raise hiring window shows a store. */
type CreatedStore = Extract<RaiseHiringSubject, { kind: 'store' }>;

type Filter = StoreRequestStatus | 'all';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'withdrawn', label: 'Withdrawn' },
  { key: 'all', label: 'All' },
];

const STATUS_STYLE: Record<StoreRequestStatus, { label: string; className: string }> = {
  // Waiting is the norm in this queue, so it stays neutral; colour marks outcomes.
  pending: { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
  approved: { label: 'Approved', className: 'bg-green-50 text-green-700' },
  rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700' },
  withdrawn: { label: 'Withdrawn', className: 'bg-gray-50 text-gray-500' },
};

const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/** GPS fixes looser than this are flagged to the approver. */
const WEAK_GPS_M = 100;

const inputClass =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

/** These endpoints answer `{ success | status, data }`; accept the array at either level. */
const listOf = <T,>(res: { data?: unknown } | undefined): T[] => {
  const body = res?.data;
  if (Array.isArray(body)) return body as T[];
  const inner = (body as { data?: unknown } | undefined)?.data;
  return Array.isArray(inner) ? (inner as T[]) : [];
};

const apiError = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const formatWhen = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

const mapsUrl = (lat: number, lng: number) => `https://www.google.com/maps?q=${lat},${lng}`;

interface Props {
  /** Toolbar slot supplied by StoreMasterPage; the Refresh button is portalled into it. */
  actionSlot?: HTMLElement | null;
  /** Reports the pending count up, for the tab badge. */
  onPendingCount?: (count: number) => void;
}

export default function StoreRequestsPage({ actionSlot, onPendingCount }: Props) {
  const projectId = useAuthStore((state) => state.selectedProjectId);
  const [filter, setFilter] = useState<Filter>('pending');
  const [items, setItems] = useState<StoreRequest[]>([]);
  const [counts, setCounts] = useState<Record<StoreRequestStatus, number>>({ pending: 0, approved: 0, rejected: 0, withdrawn: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<StoreRequest | null>(null);

  // Raising hiring for the store the last approval created. The signals the hiring screens
  // use: the shared access answer, and the shared hiring requests list, which is read only
  // where Raise is on for the project and this person may ask or raise. A person who holds
  // "Raise & cancel hiring" (the access answer's `canRaise`) raises: the list's
  // `can.confirm` is the server's word that they may raise here (Raise on, the row, a
  // 1Office sign-in), what the Stores tab reads as `raise.canRaise`. Everyone else the list
  // lets send - the Stores tick, which also gives "manage" - asks, as on the Stores tab.
  const hiringUserKey = useAuthStore((state) => hiringUserKeyOf(state.user));
  const hiringAccess = useHiringAccess(projectId, hiringUserKey);
  const hiringRequests = useHiringRequests(projectId, hiringUserKey, hiringAccess.canSeeRequests);
  const requestsShown = hiringAccess.canView && hiringRequests.status === 'ready';
  const canRaiseHiring = requestsShown && hiringAccess.canRaise && hiringRequests.can.confirm;
  const canAskHiring = requestsShown && hiringRequests.can.send && !hiringAccess.canRaise;
  // The store beside the notice, with the project it was created in; the Raise window open
  // for it; and the store hiring was raised or asked for from here, which is not offered again.
  const [created, setCreated] = useState<{ projectId: number; store: CreatedStore } | null>(null);
  const [raisingFor, setRaisingFor] = useState<{ store: CreatedStore; mode: NewStoreRaiseMode } | null>(null);
  const [raisedFor, setRaisedFor] = useState<string | null>(null);
  // The approval takes any 6 digits, but the raise refuses a PIN code that starts with 0.
  const createdMode = created && created.projectId === projectId && raisedFor !== created.store.storeId
    && /^[1-9]\d{5}$/.test(created.store.pincode)
    ? newStoreRaiseMode({
      canRaise: canRaiseHiring,
      canAsk: canAskHiring,
      storeStatus: created.store.status,
      staffingStatus: created.store.staffingStatus,
    })
    : null;

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await storeRequestsAPI.list(projectId, filter);
      const data = res.data?.data ?? {};
      setItems(Array.isArray(data.items) ? data.items : []);
      if (data.counts) {
        setCounts(data.counts);
        onPendingCount?.(Number(data.counts.pending) || 0);
      }
    } catch (err) {
      setError(apiError(err, 'Could not load store requests.'));
    } finally {
      setLoading(false);
    }
  }, [projectId, filter, onPendingCount]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshButton = (
    <button
      type="button"
      onClick={load}
      disabled={loading}
      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
    >
      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
      Refresh
    </button>
  );

  const flagsFor = (r: StoreRequest): { text: string; tone: 'warn' | 'danger' }[] => {
    const flags: { text: string; tone: 'warn' | 'danger' }[] = [];
    const dupes = r.review?.duplicates ?? [];
    if (dupes.some((d) => d.sameCode)) flags.push({ text: 'Store code already exists', tone: 'danger' });
    else if (dupes.length > 0) flags.push({ text: `${dupes.length} possible duplicate${dupes.length === 1 ? '' : 's'}`, tone: 'warn' });
    if (r.locationMocked) flags.push({ text: 'Mock location', tone: 'danger' });
    if (r.gpsAccuracy != null && r.gpsAccuracy > WEAK_GPS_M) flags.push({ text: `Weak GPS (±${Math.round(r.gpsAccuracy)} m)`, tone: 'warn' });
    if (!r.identityVerified) flags.push({ text: 'Sender not verified', tone: 'warn' });
    return flags;
  };

  return (
    <div className="space-y-4">
      {actionSlot ? createPortal(refreshButton, actionSlot) : <div className="flex justify-end">{refreshButton}</div>}

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => {
          const active = f.key === filter;
          const count = f.key === 'all' ? null : counts[f.key];
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                active ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
              {count != null && <span className="ml-1 text-gray-400">({count})</span>}
            </button>
          );
        })}
      </div>

      {notice && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span>{notice}</span>
            {created && createdMode && (
              <button
                type="button"
                onClick={() => setRaisingFor({ store: created.store, mode: createdMode })}
                className="rounded-lg border border-green-300 bg-white px-3 py-1 text-sm font-medium text-green-800 hover:bg-green-100"
              >
                {createdMode === 'raise' ? STORE_ADDED_WORDS.raise : STORE_ADDED_WORDS.ask}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setNotice(null);
              setCreated(null);
            }}
            className="text-green-700 hover:text-green-900"
          >
            ×
          </button>
        </div>
      )}
      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-5 py-3">Store</th>
                <th className="whitespace-nowrap px-5 py-3">Location</th>
                <th className="whitespace-nowrap px-5 py-3">Raised by</th>
                <th className="whitespace-nowrap px-5 py-3">Raised on</th>
                <th className="whitespace-nowrap px-5 py-3">Checks</th>
                <th className="whitespace-nowrap px-5 py-3">Status</th>
                <th className="whitespace-nowrap px-5 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-gray-400">
                    <InlineLoader label="Loading…" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-10 text-center text-gray-500">
                    {filter === 'pending' ? 'No store requests are waiting for review.' : 'Nothing here yet.'}
                  </td>
                </tr>
              ) : (
                items.map((r) => {
                  const flags = r.status === 'pending' ? flagsFor(r) : [];
                  return (
                    <tr key={r.id} className="align-top">
                      <td className="px-5 py-3">
                        <div className="font-medium text-gray-900">{r.storeName}</div>
                        <div className="text-xs text-gray-500">
                          {[r.storeCode, r.trade, r.storeCategoryName].filter(Boolean).join(' · ') || 'No code yet'}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="text-gray-800">{[r.city, r.state].filter(Boolean).join(', ')}</div>
                        <div className="max-w-xs truncate text-xs text-gray-500" title={r.address}>
                          {r.address} · {r.pinCode}
                        </div>
                        <a
                          href={mapsUrl(r.latitude, r.longitude)}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"
                        >
                          <MapPin className="h-3 w-3" /> Map
                        </a>
                      </td>
                      <td className="px-5 py-3">
                        <div className="text-gray-800">{r.requesterName || r.requesterCode}</div>
                        <div className="text-xs text-gray-500">
                          {[r.requesterCode, r.requesterDesignation].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-5 py-3 text-gray-600">{formatWhen(r.createdAt)}</td>
                      <td className="px-5 py-3">
                        {flags.length === 0 ? (
                          <span className="text-gray-300">—</span>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {flags.map((f) => (
                              <span
                                key={f.text}
                                className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                                  f.tone === 'danger' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'
                                }`}
                              >
                                {f.text}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status].className}`}>
                          {STATUS_STYLE[r.status].label}
                        </span>
                        {r.status !== 'pending' && (
                          <div className="mt-1 max-w-xs text-xs text-gray-500">
                            {r.decidedByName || r.decidedBy ? `${r.decidedByName || r.decidedBy} · ` : ''}
                            {formatWhen(r.decidedAt)}
                            {r.status === 'approved' && r.createdStoreName && <div>Created: {r.createdStoreName}</div>}
                            {r.status === 'approved' && r.requesterMapping === 'moved' && <div>Requester moved to this store</div>}
                            {r.status === 'approved' && r.requesterMapping === 'skipped' && <div>Requester not mapped</div>}
                            {r.decisionNote && r.status !== 'withdrawn' && <div className="italic">“{r.decisionNote}”</div>}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-center"><TableActionGroup>
                        {r.status === 'pending' && (
                          <TableActionButton action="review" label="Review" title="Review store request"
                            type="button"
                            onClick={() => setReviewing(r)} />
                        )}
                      </TableActionGroup></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {reviewing && projectId && (
        <ReviewModal
          key={reviewing.id}
          request={reviewing}
          projectId={projectId}
          onClose={() => setReviewing(null)}
          onDecided={(message, store) => {
            setReviewing(null);
            setNotice(message);
            setCreated(store && projectId ? { projectId, store } : null);
            load();
          }}
        />
      )}

      {/* The Raise hiring window for the store just created, with the reason New store. */}
      {raisingFor && projectId && (
        <RaiseHiringModal
          mode={raisingFor.mode}
          projectId={projectId}
          subject={raisingFor.store}
          fixedReason="new_store"
          onClose={() => setRaisingFor(null)}
          // A request stands for it: the window shows how it went, and the notice no longer offers
          // it. One closed with nothing created (refused, not sent) leaves the offer in place.
          onChanged={(link) => setRaisedFor(link.closedAs === 'create_failed' ? null : raisingFor.store.storeId)}
          onAsked={(request) => {
            setRaisedFor(raisingFor.store.storeId);
            hiringRequests.apply(request);
          }}
          onOutOfDate={hiringRequests.reload}
          // Hiring, or raising, was switched off under the window: it closes, and the offer goes with it.
          onHiringGone={() => {
            setRaisingFor(null);
            forgetHiringAccess(projectId);
          }}
          onRaiseGone={() => {
            setRaisingFor(null);
            forgetHiringRequests(projectId);
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────── review modal ─────────────────────────────── */

interface FormState {
  storeName: string;
  storeCode: string;
  trade: string;
  storeCategory: string;
  storeStatus: string;
  attendanceRadius: string;
  address: string;
  state: string;
  city: string;
  pinCode: string;
  latitude: string;
  longitude: string;
  shiftStartTime: string;
  storeWeekOff: string;
  compliancePolicy: string;
  customFields: Record<string, unknown>;
  mapRequester: boolean;
  note: string;
}

const initialForm = (r: StoreRequest): FormState => ({
  storeName: r.storeName,
  storeCode: r.storeCode ?? '',
  trade: r.trade ?? '',
  storeCategory: r.storeCategory != null ? String(r.storeCategory) : '',
  storeStatus: 'Active',
  attendanceRadius: '100',
  address: r.address,
  state: r.state,
  city: r.city,
  pinCode: r.pinCode,
  latitude: String(r.latitude),
  longitude: String(r.longitude),
  shiftStartTime: r.shiftStartTime ?? '',
  storeWeekOff: r.storeWeekOff != null ? String(r.storeWeekOff) : '',
  compliancePolicy: '',
  customFields: { ...(r.customFields ?? {}) },
  mapRequester: true,
  note: '',
});

/** Options for a master-list <select>, always including the current value so an edit never blanks it. */
const withCurrent = (names: string[], current: string): string[] => {
  if (!current || names.some((n) => normalizeLocationKey(n) === normalizeLocationKey(current))) return names;
  return [current, ...names];
};

function ReviewModal({
  request,
  projectId,
  onClose,
  onDecided,
}: {
  request: StoreRequest;
  projectId: number;
  onClose: () => void;
  /** `store`: the store an approval created, when the server named it. */
  onDecided: (message: string, store?: CreatedStore) => void;
}) {
  const [form, setForm] = useState<FormState>(() => initialForm(request));
  const [states, setStates] = useState<{ stateId: number; stateName: string }[]>([]);
  const [cities, setCities] = useState<{ cityId: number; cityName: string }[]>([]);
  const [categories, setCategories] = useState<{ id: number; category_name: string }[]>([]);
  const [trades, setTrades] = useState<string[]>([]);
  const [fieldDefs, setFieldDefs] = useState<StoreCustomField[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  // The masters the form picks from. Each fails independently to an empty list; the
  // current values stay selectable either way (withCurrent), so nothing blanks.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      locationsAPI.getStates(),
      categoriesAPI.getAll(projectId),
      storeTradesAPI.list(projectId),
      storeCustomFieldsAPI.list(projectId),
    ]).then(([s, c, t, f]) => {
      if (cancelled) return;
      if (s.status === 'fulfilled') setStates(listOf(s.value));
      if (c.status === 'fulfilled') setCategories(listOf(c.value));
      if (t.status === 'fulfilled') setTrades(listOf<{ name: string }>(t.value).map((x) => x.name));
      if (f.status === 'fulfilled') {
        const defs = listOf<StoreCustomField>(f.value);
        setFieldDefs(defs);
        // A field retired since submission would be refused as unknown; drop its value.
        const live = new Set(defs.map((d) => d.id));
        setForm((prev) => ({
          ...prev,
          customFields: Object.fromEntries(Object.entries(prev.customFields).filter(([k]) => live.has(k))),
        }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const selectedState = useMemo(
    () => states.find((s) => normalizeLocationKey(s.stateName) === normalizeLocationKey(form.state)),
    [states, form.state],
  );

  useEffect(() => {
    if (!selectedState) {
      setCities([]);
      return undefined;
    }
    let cancelled = false;
    locationsAPI
      .getCitiesByStateId(selectedState.stateId)
      .then((res) => {
        if (!cancelled) setCities(listOf(res));
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedState]);

  const review = request.review;
  const who = request.requesterName || request.requesterCode;
  const currentStores = review?.requesterStores ?? [];
  const willMove = form.mapRequester && review?.predictedMapping === 'moved';

  const mappingText = !form.mapRequester
    ? 'The store will be created with nobody mapped to it.'
    : willMove
      ? `${who}'s role allows only one store, so approving MOVES them here from ${currentStores.map((s) => s.name).join(', ')}.`
      : currentStores.length > 0
        ? `${who}'s role allows multiple stores. This store is added; they stay mapped to their ${currentStores.length} other store${currentStores.length === 1 ? '' : 's'}.`
        : `${who} is not mapped to any store yet, so this becomes their store.`;

  const approve = async () => {
    const problems: string[] = [];
    if (!form.storeName.trim()) problems.push('Store name is required.');
    if (!form.storeCode.trim()) problems.push('Store code is required to approve.');
    if (!form.address.trim()) problems.push('Address is required.');
    if (!form.state || !form.city) problems.push('State and city are required.');
    if (!/^\d{6}$/.test(form.pinCode.replace(/\s+/g, ''))) problems.push('PIN code must be 6 digits.');
    const lat = Number(form.latitude);
    const lng = Number(form.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) problems.push('Latitude and longitude must be valid numbers.');
    const radius = Number(form.attendanceRadius);
    if (!Number.isInteger(radius) || radius < 10) problems.push('Attendance radius must be a whole number of metres (10 or more).');
    if (problems.length > 0) {
      setError(problems.join(' '));
      return;
    }

    const body: StoreRequestApproval = {
      storeName: form.storeName.trim(),
      storeCode: form.storeCode.trim(),
      trade: form.trade || null,
      storeCategory: form.storeCategory ? Number(form.storeCategory) : null,
      address: form.address.trim(),
      state: form.state,
      city: form.city,
      pinCode: form.pinCode.replace(/\s+/g, ''),
      latitude: lat,
      longitude: lng,
      shiftStartTime: form.shiftStartTime || null,
      storeWeekOff: form.storeWeekOff === '' ? null : Number(form.storeWeekOff),
      customFields: form.customFields,
      storeStatus: form.storeStatus,
      attendanceRadius: radius,
      compliancePolicy: form.compliancePolicy.trim() || null,
      mapRequester: form.mapRequester,
      note: form.note.trim() || null,
    };

    setSaving(true);
    setError(null);
    try {
      const res = await storeRequestsAPI.approve(projectId, request.id, body);
      const answer = res.data?.data as StoreRequestApproved | undefined;
      // The store as saved: the fields just confirmed, and the Status and Staffing Status the
      // server read back after creating it. Without those it is offered nothing.
      onDecided(res.data?.message || 'Store created.', answer?.storeId ? {
        kind: 'store',
        storeId: String(answer.storeId),
        storeName: body.storeName,
        storeCode: body.storeCode,
        address: body.address,
        city: body.city,
        state: body.state,
        pincode: body.pinCode,
        hasMapLocation: true,
        status: answer.storeStatus ?? '',
        staffingStatus: answer.staffingStatus ?? '',
      } : undefined);
    } catch (err) {
      setError(apiError(err, 'Could not approve this request.'));
    } finally {
      setSaving(false);
    }
  };

  const reject = async () => {
    if (!reason.trim()) {
      setError('Enter a reason so the requester knows what to fix.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await storeRequestsAPI.reject(projectId, request.id, reason.trim());
      onDecided(`Request for "${request.storeName}" rejected. ${who} has been notified.`);
    } catch (err) {
      setError(apiError(err, 'Could not reject this request.'));
    } finally {
      setSaving(false);
    }
  };

  const footer = rejecting ? (
    <div className="flex w-full flex-col gap-2">
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder="Why is this request being rejected? The requester sees this."
        className={inputClass}
        autoFocus
      />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setRejecting(false)} disabled={saving} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Back
        </button>
        <button type="button" onClick={reject} disabled={saving} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60">
          {saving ? 'Rejecting…' : 'Reject request'}
        </button>
      </div>
    </div>
  ) : (
    <div className="flex w-full items-center justify-between gap-2">
      <button
        type="button"
        onClick={() => {
          setError(null);
          setRejecting(true);
        }}
        disabled={saving}
        className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
      >
        Reject…
      </button>
      <div className="flex gap-2">
        <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="button" onClick={approve} disabled={saving} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60">
          {saving ? 'Creating…' : 'Approve & create store'}
        </button>
      </div>
    </div>
  );

  const stateOptions = withCurrent(states.map((s) => s.stateName), form.state);
  const cityOptions = withCurrent(cities.map((c) => c.cityName), form.city);
  const tradeOptions = withCurrent(trades, form.trade);

  return (
    <Modal isOpen onClose={saving ? () => undefined : onClose} title={`Review store request #${request.id}`} size="xl" footer={footer}>
      <div className="space-y-5">
        {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        {/* Who raised it, and what approval does to their mapping. */}
        <section className="rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm">
          <div className="font-medium text-gray-900">
            {who} <span className="font-normal text-gray-500">({[request.requesterCode, request.requesterDesignation].filter(Boolean).join(' · ')})</span>
          </div>
          <div className="text-xs text-gray-500">Submitted {formatWhen(request.createdAt)}</div>
          {!request.identityVerified && (
            <div className="mt-1 text-xs text-amber-700">The app could not verify the sender's identity with 1Office when this was submitted.</div>
          )}
          {request.remark && <div className="mt-2 text-gray-700">“{request.remark}”</div>}
          <label className="mt-3 flex items-start gap-2">
            <input type="checkbox" className="mt-0.5" checked={form.mapRequester} onChange={(e) => set('mapRequester', e.target.checked)} />
            <span>
              <span className="font-medium text-gray-800">Map {who} to this store</span>
              <span className={`block text-xs ${willMove ? 'text-amber-700' : 'text-gray-500'}`}>{mappingText}</span>
            </span>
          </label>
        </section>

        {(review?.duplicates.length ?? 0) > 0 && (
          <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
            <div className="mb-2 flex items-center gap-2 font-medium text-amber-900">
              <AlertTriangle className="h-4 w-4" /> Possible duplicates — check before approving
            </div>
            <ul className="space-y-1 text-amber-900">
              {review?.duplicates.map((d) => (
                <li key={`${d.kind}-${d.id}`}>
                  <span className="font-medium">{d.name}</span>
                  {d.code ? ` (${d.code})` : ''}
                  {d.sameCode ? ' — same store code' : d.distanceM != null ? ` — ${d.distanceM} m away` : ''}
                  {d.detail && <span className="text-amber-800"> · {d.detail}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Store name *</label>
            <input className={inputClass} value={form.storeName} onChange={(e) => set('storeName', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Store code *</label>
            <input
              className={inputClass}
              value={form.storeCode}
              onChange={(e) => set('storeCode', e.target.value.toUpperCase())}
              placeholder="Required to approve — must be unique in this project"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Trade</label>
            <select className={inputClass} value={form.trade} onChange={(e) => set('trade', e.target.value)}>
              <option value="">— None —</option>
              {tradeOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Category</label>
            <select className={inputClass} value={form.storeCategory} onChange={(e) => set('storeCategory', e.target.value)}>
              <option value="">— None —</option>
              {categories.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.category_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Status</label>
            <select className={inputClass} value={form.storeStatus} onChange={(e) => set('storeStatus', e.target.value)}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Attendance radius (m)</label>
            <input className={inputClass} type="number" min={10} value={form.attendanceRadius} onChange={(e) => set('attendanceRadius', e.target.value)} />
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-600">Address *</label>
            <textarea className={inputClass} rows={2} value={form.address} onChange={(e) => set('address', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">State *</label>
            <select
              className={inputClass}
              value={form.state}
              onChange={(e) => setForm((prev) => ({ ...prev, state: e.target.value, city: '' }))}
            >
              <option value="">Select state</option>
              {stateOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">City *</label>
            <select className={inputClass} value={form.city} onChange={(e) => set('city', e.target.value)}>
              <option value="">Select city</option>
              {cityOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">PIN code *</label>
            <input className={inputClass} value={form.pinCode} onChange={(e) => set('pinCode', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Latitude *</label>
              <input className={inputClass} value={form.latitude} onChange={(e) => set('latitude', e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Longitude *</label>
              <input className={inputClass} value={form.longitude} onChange={(e) => set('longitude', e.target.value)} />
            </div>
          </div>
          <div className="md:col-span-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <a
              href={mapsUrl(Number(form.latitude), Number(form.longitude))}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary-600 hover:underline"
            >
              <ExternalLink className="h-3 w-3" /> Open in Google Maps
            </a>
            {request.gpsAccuracy != null && (
              <span className={request.gpsAccuracy > WEAK_GPS_M ? 'text-amber-700' : ''}>GPS accuracy at capture: ±{Math.round(request.gpsAccuracy)} m</span>
            )}
            {request.locationMocked && <span className="font-medium text-red-700">A mock-location app was active when this was captured.</span>}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Store timing</label>
            <input className={inputClass} type="time" value={form.shiftStartTime} onChange={(e) => set('shiftStartTime', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Week off</label>
            <select className={inputClass} value={form.storeWeekOff} onChange={(e) => set('storeWeekOff', e.target.value)}>
              <option value="">No fixed week off</option>
              {WEEK_DAYS.map((d, i) => (
                <option key={d} value={String(i)}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-600">Compliance policy</label>
            <textarea
              className={inputClass}
              rows={2}
              value={form.compliancePolicy}
              onChange={(e) => set('compliancePolicy', e.target.value)}
              placeholder="Optional store grooming / dress-code guidelines"
            />
          </div>
        </section>

        {fieldDefs.length > 0 && (
          <section>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Custom fields</div>
            <StoreCustomFieldInputs
              fields={fieldDefs}
              values={form.customFields}
              onChange={(fieldId, value) => setForm((prev) => ({ ...prev, customFields: { ...prev.customFields, [fieldId]: value } }))}
            />
          </section>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Note (optional)</label>
          <input className={inputClass} value={form.note} onChange={(e) => set('note', e.target.value)} placeholder="Recorded with the decision" />
        </div>
      </div>
    </Modal>
  );
}
